import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import get from 'lodash/get';
import {
    getList,
    getTrendingLists,
    createList,
    deleteList,
    saveToList,
} from 'yoda-interfaces/lib/MyList/MyList';
import FSaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { doPostSSRLogs } from 'yoda-core-components/lib/helpers/Utils/Utils';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { selectContext } from 'yoda-site-components/lib/selectors/ContextSelector';
import * as MyListActionTypes from '../actionTypes/MyListActionTypes';
import * as SavedListActionTypes from '../actionTypes/SavedListActionTypes';
import ApplicationLogger from '../helpers/ApplicationLogger';
import serverSideLogging from './ServerSideLoggingHelper';
import { createDropdownPreferences } from '../selectors/createDropdownSelector';

const logger = ApplicationLogger.getLogger();

export function* getListSaga(action) {
    const response = yield call(FSaga, getList, action, undefined, false, logger);
    const listItems = response.response || response;
    const status = get(listItems.status, '');
    const statusText = get(listItems.statusText, '');
    const serverSideLogs = {
        ssrSuccessful: false,
        ssrData: {},
    };
    const cookies = get(action, 'headers.cookies', {});
    const { cluster } = yield select(selectContext);
    doPostSSRLogs(logger, {
        requestInfo: `Yoda-Listing || List Hub || MyList Saga | getListSaga() || Status: ${status}`,
        response,
    });
    doPostSSRLogs(logger, {
        requestInfo: `Yoda-Listing || List Hub || MyList Saga | getListSaga() || Status Text: ${statusText}`,
        response,
    });
    doPostSSRLogs(logger, {
        requestInfo: `Yoda-Listing || List Hub || MyList Saga | getListSaga() || response: ${listItems}`,
        response,
    });
    const data = get(response, 'data') || get(response, 'response.data') || {};
    const payload = {
        response: data,
    };
    payload.isLoading = false;
    const statusCode = get(response, 'status') || get(response, 'response.status');
    if (statusCode && statusCode === 200) {
        payload.isSuccess = true;
        serverSideLogs.ssrData = data;
        yield all([
            put({ type: MyListActionTypes.GET_LIST_SUCCESS, payload }),
            put({ type: MyListActionTypes.GET_SITE_LIST_SUCCESS, listItems }),
        ]);
    } else {
        payload.isSuccess = false;
        payload.error = get(response, 'data') || get(response, 'response.data') || {};
        serverSideLogs.ssrData = payload.error;
        yield put({
            type: MyListActionTypes.GET_LIST_ERROR,
            payload,
        });
    }
    serverSideLogs.ssrSuccessful = payload.isSuccess;
    serverSideLogging(serverSideLogs, cookies, cluster);
}

export function* getTrendingListSaga(action) {
    const serverSideLogs = {
        ssrSuccessful: false,
        ssrData: {},
    };
    const cookies = get(action, 'headers.cookies', {});
    try {
        const response = yield call(getTrendingLists, action, undefined, false);
        const data = get(response, 'data') || get(response, 'response.data') || {};
        const payload = {
            response: data,
        };
        payload.isTrendingLoading = false;
        const statusCode = get(response, 'status') || get(response, 'response.status');
        if (statusCode && statusCode === 200) {
            payload.isSuccess = true;
            serverSideLogs.ssrSuccessful = true;
            serverSideLogs.ssrData = payload.response;
            if (action.payload.firstId) {
                const id = action.payload.firstId;
                data.listData.sort((x, y) => {
                    if (x.id === id) {
                        return -1;
                    }
                    if (y.id === id) {
                        return 1;
                    }
                    return 0;
                });
            }
            yield put({ type: MyListActionTypes.GET_TRENDING_LIST_SUCCESS, payload });
        } else {
            payload.isSuccess = false;
            serverSideLogs.ssrSuccessful = false;
            payload.error = get(response, 'data') || get(response, 'response.data', {});
            serverSideLogs.ssrData = payload.error;
            yield put({
                type: MyListActionTypes.GET_TRENDING_LIST_ERROR,
                payload,
            });
        }
    } catch (error) {
        const errorPayload = {
            error: { errorMessage: error.message, errorCode: 'EXCEPTION_IN_MY_LIST_SAGA' },
        };
        serverSideLogs.ssrSuccessful = false;
        serverSideLogs.ssrData = error;
        yield put({ type: MyListActionTypes.GET_TRENDING_LIST_ERROR, payload: errorPayload });
    }
    const { cluster } = yield select(selectContext);
    serverSideLogging(serverSideLogs, cookies, cluster);
}

function sortByName(userList) {
    const [first, ...rdata] = userList;
    rdata.sort(({ name: a }, { name: b }) => {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
    });
    return [first, ...rdata];
}

export function* createListSaga(action) {
    const response = yield call(FSaga, createList, action, undefined, false);
    const payload = {
        response: response.data || response.response.data,
    };
    payload.isLoading = false;
    payload.requestFrom = 'POST';
    try {
        if (response.response.status === 201 || response.status === 201) {
            payload.isSuccess = true;
            const createdItem = payload.response;
            // Add new list to Token Provider
            try {
                let userList = JSON.parse(TokenProvider.get('DP_USER_LIST'));
                userList.push({
                    id: createdItem.id,
                    name: createdItem.name,
                });
                userList = sortByName(userList);
                TokenProvider.set('DP_USER_LIST', JSON.stringify(userList));
            } catch (e) {
                /* eslint-disable no-console */
                console.log('Some error');
            }
            yield all([
                put({ type: MyListActionTypes.CREATE_LIST_SUCCESS_EVENT, payload }),
                put({ type: MyListActionTypes.CREATE_LIST_SUCCESS, payload }),
                put({ type: MyListActionTypes.GET_LIST, payload }),
            ]);
        } else {
            payload.isSuccess = false;
            const error = payload.response;
            const preferences = yield select(createDropdownPreferences);
            const errorMessagesMapping = get(preferences, 'errorMessages', {});
            const errorCode = get(error, 'errorCode', '');
            payload.response = {
                errorCode,
                errorMessage:
                    errorMessagesMapping[errorCode] ||
                    error.errorMessage ||
                    errorMessagesMapping.DEFAULT,
                serviceErrorMessage: get(error, 'errorMessage'),
            };
            yield all([put({ type: MyListActionTypes.CREATE_LIST_ERROR, payload })]);
        }
    } catch (error) {
        const errorPayload = {
            response: {
                errorMessage: error.message,
                errorCode: 'EXCEPTION_IN_CREATE_LIST_SAGA',
            },
            isSuccess: false,
        };
        yield put({ type: MyListActionTypes.CREATE_LIST_ERROR, payload: errorPayload });
    }
}

export function* deleteListSaga(action) {
    const response = yield call(FSaga, deleteList, action, undefined, false);
    const payload = {
        deleteId: action.payload.listId,
    };
    payload.isLoading = false;
    payload.requestFrom = 'DELETE';
    if (response.status === 204 || response.response.status === 204) {
        payload.isSuccess = true;
        // Remove List from Token Provider
        try {
            let userList = [];
            userList = JSON.parse(TokenProvider.get('DP_USER_LIST'));
            userList = userList.filter((list) => list.id !== payload.deleteId);
            TokenProvider.set('DP_USER_LIST', JSON.stringify(userList));
        } catch (e) {
            console.log('Some error'); /* eslint-disable no-console */
        }
        yield all([
            put({ type: MyListActionTypes.DELETE_LIST_SUCCESS_EVENT, payload }),
            put({ type: MyListActionTypes.DELETE_LIST_SUCCESS, payload }),
            put({ type: MyListActionTypes.GET_LIST, payload }),
        ]);
    } else {
        const deletePayload = {
            isSuccess: false,
            response: {
                errorCode: get(
                    response,
                    'response.data.errorCode',
                    'SRV_DOWN_STREAM_API_DOWN_ERROR'
                ),
                errorMessage: get(response, 'response.data.errorMessage', ''),
            },
        };

        yield put({
            type: MyListActionTypes.DELETE_LIST_ERROR,
            payload: deletePayload,
        });
    }
}

export function* updateListSaga(action) {
    const response = yield call(FSaga, saveToList, action, undefined, false);
    const payload = {
        listId: action.payload.listId,
    };
    payload.isLoading = false;
    payload.requestFrom = 'UPDATE';
    if (response.response.status === 204 || response.status === 204) {
        payload.isSuccess = true;
        payload.list = {};
        action.payload.params.forEach((item) => {
            if (item.op === 'replace') {
                if (item.path === '/name') {
                    payload.list.name = item.value;
                } else if (item.path === '/eventDate') {
                    payload.list.eventDate = item.value;
                } else if (item.path === '/isPublic') {
                    payload.list.isPublic = item.value;
                }
            }
        });
        const updateList = {
            listName: payload.list.name,
            eventDate: payload.list.eventDate,
            isPublic: payload.list.isPublic,
        };
        // Update DP_USER_LIST in localstorage as left navigation uses it
        let userList = JSON.parse(TokenProvider.get('DP_USER_LIST'));
        const modifiedListIndex = userList.indexOf(
            userList.find((listItem) => listItem.id === payload.listId)
        );
        modifiedListIndex >= 0 && (userList[modifiedListIndex].name = payload.list.name);
        userList = sortByName(userList);
        TokenProvider.set('DP_USER_LIST', JSON.stringify(userList));
        yield all([
            put({ type: MyListActionTypes.SAVE_TO_LIST_SUCCESS, payload }),
            put({ type: SavedListActionTypes.UPDATE_SAVED_LIST_SUCCESS, updateList }),
        ]);
    } else {
        const preferences = yield select(createDropdownPreferences);
        const errorMessagesMapping = get(preferences, 'errorMessages', {});
        const errorCode = get(
            response,
            'response.data.errorCode',
            'SRV_DOWN_STREAM_API_DOWN_ERROR'
        );
        const listPayload = {
            isSuccess: false,
            response: {
                errorCode,
                errorMessage:
                    errorMessagesMapping[errorCode] ||
                    get(response, 'response.data.errorMessage', '') ||
                    errorMessagesMapping.DEFAULT,
            },
        };
        yield all([put({ type: MyListActionTypes.SAVE_TO_LIST_ERROR, payload: listPayload })]);
    }
}

export function* loadTrendingItems(action) {
    const { id, token, accountId, sort } = action.payload.queryParams;
    const params = {
        payload: {
            listId: id,
            token,
            accountId,
            isTrendingList: true,
        },
    };
    if (sort) {
        params.payload.params = { sort };
    }
    const response = yield call(getList, params, undefined, false);
    const payload = {
        response,
        queryParams: action.payload.queryParams,
    };
    payload.isLoaded = true;
    const statusCode = get(response, 'status') || get(response, 'response.status');
    if (statusCode && statusCode === 200) {
        payload.isSuccess = true;
        yield put({ type: MyListActionTypes.LOAD_TRENDING_ITEMS_SUCCESS, payload });
    } else {
        payload.isSuccess = false;
        payload.error = get(response, 'data') || get(response, 'response.data', {});
        yield put({
            type: MyListActionTypes.LOAD_TRENDING_ITEMS_ERROR,
            payload,
        });
    }
}

const watchMyListSaga = function* watchMyListSaga() {
    yield takeLatest(MyListActionTypes.GET_LIST, getListSaga);
    yield takeLatest(MyListActionTypes.GET_TRENDING_LIST, getTrendingListSaga);
    yield takeLatest(MyListActionTypes.CREATE_LIST, createListSaga);
    yield takeLatest(MyListActionTypes.DELETE_LIST, deleteListSaga);
    yield takeLatest(MyListActionTypes.SAVE_TO_LIST, updateListSaga);
    yield takeLatest(MyListActionTypes.LOAD_TRENDING_ITEMS, loadTrendingItems);
};
watchMyListSaga.sagaName = 'watchMyListSaga';

export default watchMyListSaga;
